import { Head, useForm } from '@inertiajs/react'
import { withAppLayout, PageHeading } from '@/components/layout'
import { Card, Field, Label, Input, Select, Button, ErrorMessage } from '@/components/ui'
import { CredentialForm } from '@/components/merchants/CredentialForm'
import { AssignmentList } from '@/components/common/AssignmentList'
import { useTranslations } from '@/lib/i18n'

interface MerchantData {
  id: number | null
  name: string
  status: string
  user_ids: number[]
}

interface AssignableUser {
  id: number
  name: string
  email: string
  role: string
}

interface MerchantFormProps {
  merchant: MerchantData
  statuses: string[]
  assignable_users: AssignableUser[]
  credential: { configured: boolean }
  errors: Record<string, string>
}

function MerchantForm({ merchant, statuses, assignable_users, credential, errors }: MerchantFormProps) {
  const { t } = useTranslations()
  const isEdit = merchant.id !== null
  const form = useForm({
    name: merchant.name,
    status: merchant.status,
    user_ids: merchant.user_ids,
  })

  function submit(event: React.FormEvent) {
    event.preventDefault()
    form.transform((data) => ({ merchant: data }))
    if (isEdit) form.patch(`/merchants/${merchant.id}`)
    else form.post('/merchants')
  }

  function toggleUser(id: number, checked: boolean) {
    form.setData('user_ids', checked ? [...form.data.user_ids, id] : form.data.user_ids.filter((x) => x !== id))
  }

  const title = isEdit ? t('merchants.edit_title') : t('merchants.new_title')

  return (
    <>
      <Head title={title} />
      <PageHeading title={title} actions={<Button plain href="/merchants">{t('common.back')}</Button>} />

      <Card className="mt-8 max-w-2xl">
        <form onSubmit={submit} className="flex flex-col gap-5">
          <Field>
            <Label>{t('merchants.name')}</Label>
            <Input value={form.data.name} onChange={(e) => form.setData('name', e.target.value)} autoFocus />
            {errors.name && <ErrorMessage>{errors.name}</ErrorMessage>}
          </Field>

          <Field>
            <Label>{t('merchants.status')}</Label>
            <Select value={form.data.status} onChange={(e) => form.setData('status', e.target.value)}>
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {t(`statuses.${status}`)}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label>{t('merchants.assigned_users')}</Label>
            <AssignmentList
              items={assignable_users.map((u) => ({ id: u.id, label: u.name, sublabel: u.email }))}
              selected={form.data.user_ids}
              onToggle={toggleUser}
              searchPlaceholder={t('common.search')}
            />
          </Field>

          <div className="flex gap-2">
            <Button type="submit" color="brand" disabled={form.processing}>
              {isEdit ? t('common.save') : t('common.create')}
            </Button>
          </div>
        </form>
      </Card>

      {isEdit && merchant.id !== null && (
        <div className="max-w-2xl">
          <CredentialForm merchantId={merchant.id} configured={credential.configured} />
        </div>
      )}
    </>
  )
}

MerchantForm.layout = withAppLayout

export default MerchantForm
